import Diff from 'deep-diff';
import React, { useEffect, useState } from 'react';

import { BaseSlider } from '$partials/fields/slider';
import {
    ceilToNearestZero,
    floorToNearestZero,
    fromLogScale,
    roundToNearestZero,
    toLogScale,
} from '$utilities/maths';
import { useExchangeRate } from '$contexts/exchange-rate';

/* ========================================================================= */
/* Utilities */
/* ========================================================================= */

/** @type {(value: number, currency: string) => string} */
export const formatForDisplay = (value, currency) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'GBP', notation: 'compact' }).format(value);

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/filters/individual.d.ts').PriceSlider}
 */
export const PriceSlider = ({
    defaultValue,
    label,
    max = 1,
    min = 0,
    name,
    onChange,
    value,
}) => {
    // Rounded values (linear)
    const minValue = floorToNearestZero(min);
    const maxValue = ceilToNearestZero(max);
    const defaultMin = defaultValue?.length === 2 ? floorToNearestZero(defaultValue[0]) : minValue;
    const defaultMax = defaultValue?.length === 2 ? ceilToNearestZero(defaultValue[1]) : maxValue;
    const selectedMinValue = value?.length === 2 ? floorToNearestZero(value[0]) : defaultMin;
    const selectedMaxValue = value?.length === 2 ? ceilToNearestZero(value[1]) : defaultMax;

    // Slider values (log)
    const sliderMin = toLogScale(minValue);
    const sliderMax = toLogScale(maxValue);
    const [sliderState, setSliderState] = useState([toLogScale(selectedMinValue), toLogScale(selectedMaxValue)]);
    const [sliderStateImmediate, setSliderStateImmediate] = useState(sliderState);

    useEffect(() => {
        const nextState = [
            roundToNearestZero(fromLogScale(sliderState[0])),
            roundToNearestZero(fromLogScale(sliderState[1]))
        ];

        const comparisonValue = [
            roundToNearestZero(fromLogScale(toLogScale(selectedMinValue))),
            roundToNearestZero(fromLogScale(toLogScale(selectedMaxValue)))
        ];

        if (Diff(nextState, comparisonValue)) {
            onChange(nextState);
        }
    }, [sliderState]);

    const { currency, convertToCurrency } = useExchangeRate();

    const displayMin = formatForDisplay(convertToCurrency({ from: 'GBP', to: currency, amount: roundToNearestZero(fromLogScale(sliderStateImmediate[0]) )}), currency);
    const displayMax = formatForDisplay(convertToCurrency({ from: 'GBP', to: currency, amount: roundToNearestZero(fromLogScale(sliderStateImmediate[1]) )}), currency);

    /**
     * Required to override the default aria values added by the slider component
     */
    const forceAriaValues = () => {
        const sliders = document.querySelectorAll('[role="slider"]');
        sliders?.[0]?.setAttribute('aria-valuemin', String(minValue));
        sliders?.[0]?.setAttribute('aria-valuemax', String(maxValue));
        sliders?.[0]?.setAttribute('aria-valuenow', String(selectedMinValue));
        sliders?.[1]?.setAttribute('aria-valuemin', String(minValue));
        sliders?.[1]?.setAttribute('aria-valuemax', String(maxValue));
        sliders?.[1]?.setAttribute('aria-valuenow', String(selectedMaxValue));
    };

    /**
     * Handle slider value change
     * @param {number[]} value
     */
    const handleSliderChange = async (value) => {
        await setSliderState(value);

        setTimeout(forceAriaValues, 0);
    };

    useEffect(() => {
        setTimeout(forceAriaValues, 0);
    }, []);

    return (
        <fieldset>
            {label &&
                <legend className='block mb-16 type-style-eyebrow'>
                    {label}
                </legend>
            }

            <BaseSlider
                max={sliderMax}
                min={sliderMin}
                name={name}
                onChange={setSliderStateImmediate}
                onDebouncedChange={handleSliderChange}
                step={0.01}
                value={sliderState} />

            <div className='flex w-full mt-8 whitespace-nowrap type-style-eyebrow'>
                <div className='w-full'>
                    <div className='text-[9px]'>
                        Min
                    </div>
                    <div>
                        {displayMin}
                    </div>
                </div>
                <div className='text-right'>
                    <div className='text-[9px]'>
                        Max
                    </div>
                    <div>
                        {displayMax}
                    </div>
                </div>
            </div>
        </fieldset>
    );
};

/* ========================================================================= */
/* Export(s) */
/* ========================================================================= */

export default PriceSlider;
